import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router'
import ProductCard from './ProductCard'
import OrderStatusModal from './components/OrderStatusModal'
import StoreBasket from '../ColteneStore/StoreBasket'
import Breadcrumb from '../common/Breadcrumb'
import SimpleButton from '../common/SimpleButton'
import Dropdown from '../common/Dropdown'
import LoadingSpinner from '../common/LoadingSpinner'
import SearchWithFilter from '../common/SearchWithFilter'
import EmptyPageResults from '../common/EmptyPageResults'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import WrappedTabs from '../common/WrappedTabs'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import { clearBasket, loadStoreDigitalProducts, loadStorePhyiscalProducts } from '../../actions/colteneStore'
import { clearBackNavigationState } from '../../actions/backNavigationState'
import { getUserSubscriptions } from '../../utils/requests/productsAPI'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import './coltenestore.scss'

const sortingOptions = [
  {
    key: 'amount_asc',
    label: 'sortable_context.price_ascendent',
  },
  {
    key: 'amount_desc',
    label: 'sortable_context.price_descendent',
  },
]

const ColteneStore = (props) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const canGoBack = location.state && location.state.withBackNavigation
  const { basket, isLoading } = props
  const [showStoreBasket, setShowStoreBasket] = useState(false)
  const [showSuccessOrderModal, setShowSuccessOrderModal] = useState(false)
  const [successOrderId, setSuccessOrderId] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [selectedSortingOption, setSelectedSortingOption] = useState(null)
  const [searchParams, setSearchParams] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [canLoadMorePhysicalProducts, setCanLoadMorePhysicalProducts] = useState(false)
  const [canLoadMoreDigitalProducts, setCanLoadMoreDigitalProducts] = useState(false)
  const [physicalProducts, setPhysicalProducts] = useState([])
  const [digitalProducts, setDigitalProducts] = useState([])
  const [activeSubscriptions, setActiveSubscriptions] = useState([])
  const [currentPhysicalSearchPage, setCurrentPhysicalSearchPage] = useState(1)
  const [currentDigitalSearchPage, setCurrentDigitalSearchPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [subscriptionsLoaded, setSubscriptionsLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState(null)
  const ongoingSubscriptionStatuses = ['active', 'trialing', 'past_due']
  const physicalTab = -1
  const digitalTab = 1

  const tabs = useMemo(
    () => [
      { id: -1, slug: 'products', name: 'nav.products' },
      { id: 1, slug: 'subscriptions', name: 'nav.subscriptions' },
    ],
    [],
  )

  useLayoutEffect(() => {
    const activeIndex = props.backNavigationState?.activeIndex
    if (activeIndex !== undefined && activeIndex !== null) {
      const tabFromBackNav = tabs.find((tab) => tab.id === activeIndex) || tabs[0]
      setActiveTab(tabFromBackNav)
      props.actions.clearBackNavigationState()
    }
  }, [tabs])

  useEffect(() => {
    try {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.colteneStorePage,
        label: 'Coltene Store Page: Page View',
      })
    } catch (error) {
      console.error('GA tracking error:', error)
    }
  }, [])

  useEffect(() => {
    setShowSpinner(isLoading)
  }, [isLoading])

  useEffect(() => {
    setPhysicalProducts(props.physicalProducts)
  }, [props.physicalProducts])

  useEffect(() => {
    setDigitalProducts(props.digitalProducts)
  }, [props.digitalProducts])

  useEffect(() => {
    getUserSubscriptions()
      .then((subscriptions) => {
        const filteredActiveSubscriptions = subscriptions?.filter((subs) => ongoingSubscriptionStatuses.includes(subs.status))?.map((s) => s.product?.id)
        setActiveSubscriptions(filteredActiveSubscriptions || [])
      })
      .finally(() => {
        setSubscriptionsLoaded(true)
      })
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const status = params.get('order_status')
    const orderId = params.get('order')
    if (status === 'successful') {
      props.actions.clearBasket()
      params.delete('order_status')
      params.delete('order')
      const nextSearch = params.toString()
      history.replace({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, location.state || undefined)
      setShowSuccessOrderModal(true)
      setSuccessOrderId(orderId)
    }
  }, [location.search, location.pathname, location.state, history])
  
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const contentParam = params.get('content')
    let selected = activeTab || tabs[0]
    if (contentParam) {
      const match = tabs.find((t) => t.slug === contentParam)
      if (match) selected = match
    }
    if (!activeTab || activeTab.id !== selected.id) {
      setActiveTab(selected)
    }
    if (params.get('content') !== selected.slug) {
      params.set('content', selected.slug)
      history.replace({ pathname: location.pathname, search: `?${params.toString()}` }, location.state || undefined)
    }
  }, [location.search, location.pathname, location.state, history, tabs])
  
  useDeepCompareEffect(() => {
    const changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setCurrentPhysicalSearchPage(1)
          setCurrentDigitalSearchPage(1)
          handleLoadProducts(true)
        }

        setShouldApplyFilters(true)
      },
      searchParams ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParams, selectedSortingOption])

  useEffect(() => {
    if (currentPhysicalSearchPage && currentPhysicalSearchPage > 1) {
      handleLoadProducts(false, physicalTab)
    }
  }, [currentPhysicalSearchPage])

  useEffect(() => {
    if (currentDigitalSearchPage && currentDigitalSearchPage > 1) {
      handleLoadProducts(false, digitalTab)
    }
  }, [currentDigitalSearchPage])

  useEffect(() => {
    setCanLoadMorePhysicalProducts(props.canLoadMorePhysicalProducts)
  }, [props.canLoadMorePhysicalProducts])

  useEffect(() => {
    setCanLoadMoreDigitalProducts(props.canLoadMoreDigitalProducts)
  }, [props.canLoadMoreDigitalProducts])

  const handleChangeTab = (tabKey) => {
    const tab = tabs.find((tab) => tab.id === tabKey)
    if (!tab) return
    
    try {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.colteneStorePage,
        label: `Coltene Store Page: Tab Switch - ${tab?.name}`,
      })
    } catch (error) {
      console.error('GA tracking error:', error)
    }
    
    setActiveTab(tab)
    const params = new URLSearchParams(location.search)
    params.set('content', tab.slug)
    history.push({ pathname: location.pathname, search: `?${params.toString()}` }, location.state || undefined)
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParams(value)
    } else if (value?.length === 0 && searchParams?.length > 0) {
      setShowSpinner(true)
      setSearchParams(null)
    }
  }

  const handleLoadProducts = (withReset, withCurrentTab) => {
    const queryParams = createQueryParams()

    const physicalProductsQueryParams = {
      ...queryParams,
      startPage: withReset ? 1 : currentPhysicalSearchPage,
    }

    const digitalProductsQueryParams = {
      ...queryParams,
      startPage: withReset ? 1 : currentDigitalSearchPage,
    }

    if (!withCurrentTab || withCurrentTab === physicalTab) {
      props.actions.loadStorePhyiscalProducts(physicalProductsQueryParams, withReset).then(() => {
        if (!activeTab || activeTab.id !== digitalTab) {
          setIsFirstLoad(false)
        }
      })
    }

    if (!withCurrentTab || withCurrentTab === digitalTab) {
      props.actions.loadStoreDigitalProducts(digitalProductsQueryParams, withReset).then(() => {
        if (activeTab && activeTab.id === digitalTab) {
          setIsFirstLoad(false)
        }
      })
    }
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParams) {
      queryParams = { ...queryParams, title: searchParams }
    }

    if (selectedSortingOption) {
      queryParams = { ...queryParams, orderBy: selectedSortingOption.key.split('_')[0], order: selectedSortingOption.key.split('_')[1] }
    }

    return queryParams
  }

  const handleLoadMorePhysicalProducts = () => {
    try {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.colteneStorePage,
        label: 'Coltene Store Page: Load More Physical Products',
      })
    } catch (error) {
      console.error('GA tracking error:', error)
    }
    
    setCurrentPhysicalSearchPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleLoadMoreDigitalProducts = () => {
    try {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.colteneStorePage,
        label: 'Coltene Store Page: Load More Digital Products',
      })
    } catch (error) {
      console.error('GA tracking error:', error)
    }
    
    setCurrentDigitalSearchPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const selectSortingOption = (optionKey) => {
    const sortOption = sortingOptions.find((field) => field.key === optionKey)
    
    try {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.colteneStorePage,
        label: `Coltene Store Page: Sort Option Selected - ${sortOption?.key}`,
      })
    } catch (error) {
      console.error('GA tracking error:', error)
    }
    
    setSelectedSortingOption(sortOption)
  }

  return (
    <div className="coltene-store-wrapper">
      <Breadcrumb path={location.pathname} withBackNavigation={canGoBack} />
      <DashboardHeader headerText={t('nav.coltene_store')} />
      <WrappedTabs tabs={tabs.map((tab) => ({ id: tab.id, name: tab.name }))} activeTab={activeTab?.id} changeTab={handleChangeTab} />
      <div className="coltene-store-header">
        <SearchWithFilter onSearch={handleSearch} />
        <div className="dropdown-area">
          <div className="sorting-label">{t('sort_by')}</div>
          <Dropdown
            className="dropdown-input"
            options={sortingOptions?.map((field) => ({ key: field.key, value: t(`${field.label}`) }))}
            initialSelectedKey={selectedSortingOption?.key}
            onChange={(orderBy) => selectSortingOption(orderBy.key)}
          />
        </div>
      </div>
      <div className="product-container">
        {activeTab &&
          (activeTab.id === digitalTab ? digitalProducts : physicalProducts).flatMap((product) =>
            product.prices.map((price) => (
              <ProductCard
                key={price.id}
                product={product}
                price={price}
                numInBasket={basket[product.id]}
                setShowStoreBasket={() => setShowStoreBasket(true)}
                activeSubscriptions={subscriptionsLoaded && activeSubscriptions}
              />
            )),
          )}
      </div>
      {!showSpinner &&
        !isFirstLoad &&
        activeTab &&
        ((activeTab.id === physicalTab && physicalProducts.length === 0) || (activeTab.id === digitalTab && digitalProducts.length === 0)) && (
          <EmptyPageResults title={t('coltene_store.cart.no_products_found')} />
        )}
      {activeTab && ((canLoadMorePhysicalProducts && activeTab.id === physicalTab) || (canLoadMoreDigitalProducts && activeTab.id === digitalTab)) && (
        <div className="load-more-wrapper d-flex flex-justify-center">
          <SimpleButton
            className="load-more-button"
            onClick={() => (activeTab.id === digitalTab ? handleLoadMoreDigitalProducts() : handleLoadMorePhysicalProducts())}
          >
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showSuccessOrderModal && <OrderStatusModal onClose={() => setShowSuccessOrderModal(false)} orderId={successOrderId} />}
      <StoreBasket toggle={showStoreBasket} onToggle={() => setShowStoreBasket(false)} />
    </div>
  )
}

function stateToProps({ colteneStore, backNavigationState }) {
  return {
    physicalProducts: colteneStore?.physicalProducts || [],
    digitalProducts: colteneStore?.digitalProducts || [],
    isLoading: colteneStore?.isLoading,
    basket: colteneStore?.basket || {},
    canLoadMorePhysicalProducts: colteneStore?.canLoadMorePhysicalProducts,
    canLoadMoreDigitalProducts: colteneStore?.canLoadMoreDigitalProducts,
    backNavigationState,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadStorePhyiscalProducts,
        loadStoreDigitalProducts,
        clearBasket,
        clearBackNavigationState,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ColteneStore)
